<script setup lang="ts">
import type { BaseTailwindColor } from '#shared/types/elementsUi.ts'
import type { ComponentSize, UIProp } from '~/types/elementsUi.ts'

interface VSwitchBlockProps {
  color?: BaseTailwindColor
  size?: ComponentSize
  leftLabel?: string
  rightLabel?: string
  srLabel: string
  disabled?: boolean
  ui?: UIProp<'base' | 'label'>
}

// NOTE: Intentionally did not use Nuxt/UI's Switch component, as it breaks in the Cookie Banner

const props = defineProps<VSwitchBlockProps>()

const modelValue = defineModel<boolean>({ required: true })

const { t, te } = useI18n()

const colorMap: Record<BaseTailwindColor, string> = {
  primary: tw`peer-checked:bg-primary`,
  secondary: tw`peer-checked:bg-secondary`,
  neutral: tw`peer-checked:bg-neutral`,
  info: tw`peer-checked:bg-info`,
  success: tw`peer-checked:bg-success`,
  warning: tw`peer-checked:bg-warning`,
  danger: tw`peer-checked:bg-danger`,
  white: tw`peer-checked:bg-white`,
  gray: tw`peer-checked:bg-gray`,
  black: tw`peer-checked:bg-black`,
  'pure-black': tw`peer-checked:bg-pure-black`,
  'dark-green': tw`peer-checked:bg-dark-green`,
  beige: tw`peer-checked:bg-beige`,
  pink: tw`peer-checked:bg-pink`
}

const computedColor = computed(() => {
  if (!props.color) {
    return colorMap.primary
  } else {
    return colorMap[props.color]
  }
})

const computedSize = computed(() => {
  if (!props.size || props.size === 'normal') {
    return {
      bar: tw`h-5 w-9`,
      thumb: tw`h-4 w-4 left-0.5`
    }
  } else if (props.size === 'small') {
    return {
      bar: tw`h-4 w-7`,
      thumb: tw`h-3 w-3 left-0.5 peer-checked:-left-0.5`
    }
  } else if (props.size === 'medium') {
    return {
      bar: tw`h-6 w-10`,
      thumb: tw`h-5 w-5 left-0.5`
    }
  } else {
    return {
      bar: tw`h-7 w-11`,
      thumb: tw`h-6 w-6 left-0.5`
    }
  }
})

const computedLabelLeft = computed(() =>
  props.leftLabel && te(props.leftLabel) ? t(props.leftLabel) : props.leftLabel
)
const computedLabelRight = computed(() =>
  props.rightLabel && te(props.rightLabel) ? t(props.rightLabel) : props.rightLabel
)

const computedSrLabel = computed(() =>
  te(props.srLabel) ? t(props.srLabel) : props.srLabel
)

const computedUi = computed(() =>
  nuxtUiMerge(props.ui, {
    base: tw`flex items-center`
  })
)
</script>

<template>
  <div :class="computedUi?.base">
    <span v-if="computedLabelLeft" class="mr-2 select-none" :class="computedUi?.label">{{
      computedLabelLeft
    }}</span>

    <label
      class="relative inline-flex cursor-pointer items-center"
      :class="disabled && 'pointer-events-none opacity-50'"
    >
      <input
        v-model="modelValue"
        type="checkbox"
        role="switch"
        class="peer sr-only"
        :disabled
        :aria-label="computedSrLabel"
      />
      <span
        class="rounded-full bg-gray-200 transition-colors peer-focus-visible:ring-2 dark:bg-gray-800"
        :class="[computedColor, computedSize.bar]"
      />
      <span
        class="absolute translate-y-[1%] rounded-full bg-white transition-transform peer-checked:translate-x-4"
        :class="computedSize.thumb"
      />
    </label>

    <span v-if="computedLabelRight" class="ml-2 select-none" :class="computedUi?.label">{{
      computedLabelRight
    }}</span>
  </div>
</template>

<style scoped></style>
